import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import i18next from 'i18next';
import { AssistantMessage, Conversation } from '../../conversation.js';
import type { Exam, ExamState } from '../../exam.js';
import { processUploadedFiles } from '../agents/uploadAgent.js';
import { deletePendingFileEntries, selectPendingCancelTargets } from '../file-intake.js';
import { isUserFacingUploadError } from '../file-upload.js';
import {
  assignmentEntries,
  buildProposalRoleMap,
  canProcessBlankExam,
  collectAssignedPendingIds,
  findAssignmentMismatches,
  findLowConfidenceAssignments,
  findMissingPendingFiles,
  formatMismatchBlock,
  formatMissingFilesBlock,
  hasStructureSource,
  isStructureConfirmed,
  listPendingFileNames,
  parseAssignmentArgs,
  structureExtractionStarted,
  type FileAssignment,
} from './file-assignment.js';
// Ensure server-side i18n is initialized before calling `i18next.t`
import '../../../i18n.js';

export const PROCESS_FILES_TOOL_NAME = 'process_files';

/**
 * The tool schema depends on the exam's state, because what an upload can still achieve changes as the
 * exam progresses:
 *  - A blank exam is only useful as the source for structure extraction, so the parameter disappears
 *    once that extraction has run — offering it afterwards only invites the agent to promise
 *    something we'd have to reject.
 *  - Every other role can be uploaded at any time, in any combination. What varies is only WHEN it is
 *    evaluated: a master solution or grading scheme is stored immediately but can only be extracted
 *    against a confirmed task structure, so without one it waits. The rules below exist so the agent
 *    tells the teacher that instead of implying the work has already happened.
 *
 * Submissions are deliberately never required. They are needed for grading, not for processing — an
 * exam can reach a confirmed structure, master solution and grading scheme with none at all.
 */
export function processFilesToolDef(examState: ExamState) {
  const blankExamAllowed = canProcessBlankExam(examState);
  const structureConfirmed = isStructureConfirmed(examState);
  const extractionStarted = structureExtractionStarted(examState);
  const submissionsExist = (examState?.uploadProgress?.submissionsFound ?? 0) > 0;
  // A section's source state is only set once at least one task actually carries content, which makes it
  // the signal for "there is something a new upload would replace" — regardless of whether that content was
  // extracted, generated or typed in the canvas. `exam.masterSolutionIds` is not available here (the schema
  // is built from the exam state alone) and would answer a different question anyway: a file was uploaded,
  // not that any task got content out of it.
  const hasMasterSolutionContent = !!examState?.masterSolutionSourceState;
  const hasGradingSchemeContent = !!examState?.gradingSchemeSourceState;
  const gradingStarted = !!examState?.hasGradingStarted;

  const solutionRules = structureConfirmed
    ? `- Die Aufgabenstruktur dieser Klausur ist bestätigt. Eine neu hochgeladene Musterlösung oder ein neues Bewertungsschema wird sofort gegen diese Struktur ausgewertet.`
    : `- Musterlösung und Bewertungsschema können jetzt hochgeladen werden, ausgewertet werden sie aber erst gegen eine bestätigte Aufgabenstruktur. Behaupte nie, eine Datei sei schon ausgewertet.`;

  const structureRules = extractionStarted
    ? `- ${structureConfirmed ? 'Die Aufgabenstruktur dieser Klausur ist erkannt und bestätigt.' : 'Die Erkennung der Aufgabenstruktur läuft bereits bzw. wartet auf die Bestätigung des Nutzers.'} Eine leere Klausur kann NICHT mehr verarbeitet werden. Wenn der Nutzer eine hochlädt, sage ihm klar, dass das jetzt nicht mehr möglich ist — die Datei bleibt unverarbeitet liegen und kann einer anderen Kategorie zugewiesen werden.`
    : `- Für diese Klausur wurde noch keine Aufgabenstruktur erkannt. Sie wird aus der leeren Klausur gelesen, wenn es eine gibt, sonst aus der ersten Schülerabgabe.
      - Die leere Klausur ist hilfreich, aber nicht zwingend erforderlich — sie kann NUR jetzt verarbeitet werden, später nicht mehr. Mit einer leeren Klausur startet die Strukturerkennung sofort und braucht keine Schülerabgaben.
      - Wenn keine leere Klausur dabei ist, frage den Nutzer einmal danach und weise darauf hin, dass sie später nicht mehr nachgereicht werden kann. Setze leereKlausurNachgefragt=true, sobald du gefragt hast oder der Nutzer gesagt hat, dass er keine hat.`;

  // Without a structure source, a master solution or grading scheme can be stored but never evaluated.
  // Processing it anyway consumes its entry in the upload buffer for no gain, and the file then has to be
  // named again later — so the whole call waits until a source is there and is then processed in one go.
  const structureSourceMissing = !extractionStarted && !submissionsExist;

  const submissionRules = structureSourceMissing
    ? `- Für diese Klausur gibt es noch keine Grundlage für die Aufgabenstruktur. Dieser Aufruf MUSS daher entweder Schülerabgaben oder eine leere Klausur enthalten — eines von beiden genügt.
      - Sind nur Musterlösung und/oder Bewertungsschema vorhanden, rufe dieses Tool NICHT auf. Frage den Nutzer zuerst nach den Schülerabgaben oder der leeren Klausur, merke dir die Dateinamen der schon hochgeladenen Dateien und rufe das Tool dann EINMAL mit allen Dateien zusammen auf. Die Dateien bleiben bis dahin im Upload-Puffer und müssen nicht erneut hochgeladen werden.`
    : submissionsExist
      ? `- Für diese Klausur liegen bereits Schülerabgaben vor. Weitere Abgaben können jederzeit dazukommen.`
      : `- Für diese Klausur liegen noch keine Schülerabgaben vor. Sie werden erst für die Bewertung gebraucht, nicht für die Verarbeitung der anderen Dateien.`;

  // What an upload does to what is already there. The two roles behave differently and neither is
  // reversible from the teacher's side, so the difference has to be said out loud: a solution REPLACES the
  // section it belongs to, submissions are ADDED to the ones already split out. Only stated where there is
  // something to replace or add to — on a first upload none of this applies.
  const consequenceRules = [
    hasMasterSolutionContent
      ? `- Diese Klausur hat bereits eine Musterlösung. Eine neu hochgeladene Musterlösung ERSETZT sie vollständig — auch von der KI generierte oder im Editor bearbeitete Inhalte gehen dabei verloren. Sage dem Nutzer das, wenn der Upload eine Musterlösung enthält und bitte ihn um Bestätigung.`
      : '',
    hasGradingSchemeContent
      ? `- Diese Klausur hat bereits ein Bewertungsschema. Ein neu hochgeladenes Bewertungsschema ERSETZT es vollständig — auch von der KI generierte oder im Editor bearbeitete Inhalte gehen dabei verloren. Sage dem Nutzer das, wenn der Upload ein Bewertungsschema enthält und bitte ihn um Bestätigung.`
      : '',
    hasGradingSchemeContent
      ? `- Wird (auch) eine neue Musterlösung hochgeladen, wird das bestehende Bewertungsschema danach automatisch an die neue Musterlösung angepasst. Kündige das als Automatik an, wenn der Upload eine Musterlösung enthält — der Nutzer muss dafür nichts tun.`
      : '',
    submissionsExist
      ? `- Neu hochgeladene Schülerabgaben werden ERGÄNZT: die bereits vorhandenen Abgaben bleiben unverändert erhalten und werden nicht ersetzt. Sage dem Nutzer das, wenn der Upload Schülerabgaben enthält.`
      : '',
    gradingStarted
      ? `- Diese Klausur wurde schon bewertet. Eine neue Musterlösung oder ein neues Bewertungsschema löst deshalb automatisch eine Neubewertung aller bewertbaren Abgaben aus, neue Schülerabgaben werden nach ihrer Verarbeitung automatisch bewertet. Kündige das als Automatik an — der Nutzer muss dafür nichts tun.`
      : '',
  ]
    .filter(Boolean)
    .join('\n      ');

  // The rules state facts. What to SAY after a call is decided per call by the tool result, which knows
  // what actually started — duplicating the instructions here made the agent say everything twice. The
  // exception is the consequences block: which of them apply follows from the FILES in the call, so the
  // result names them too, and the agent must not drop them just because the rest of these rules is silent.
  const phaseRules = `${submissionRules}\n      ${structureRules}\n      ${solutionRules}${
    consequenceRules ? `\n      ${consequenceRules}` : ''
  }`;

  return {
    type: 'function',
    function: {
      name: PROCESS_FILES_TOOL_NAME,
      description: `Verarbeitet die vom Nutzer hochgeladenen Dateien. Dieses Tool MUSS aufgerufen werden, nachdem Dateien hochgeladen wurden — es ist der einzige Weg, hochgeladene Dateien weiterzuverarbeiten.

RULES:
      - Ist die Zuordnung der Dateien klar (der Nutzer hat sie ausdrücklich genannt, die Dateinamen sind eindeutig, oder der Nutzer hat einen Zuordnungsvorschlag bestätigt), rufe dieses Tool direkt mit userExplicitlyConfirmed=true auf.
      - Ist die Zuordnung unklar, rufe zuerst send_assignment_confirmation_message auf, damit der Nutzer sie im Drag-and-Drop-Bereich prüfen kann. Erst nach seiner Bestätigung rufst du dieses Tool mit userExplicitlyConfirmed=true auf — und dann auf keinen Fall erneut send_assignment_confirmation_message.
      - Möchte der Nutzer den Upload abbrechen, setze cancelUpload=true. Ohne cancelFileNames werden alle unverarbeiteten Dateien gelöscht, sonst nur die genannten.
      - Abbrechen und Verarbeiten gehen in EINEM Aufruf: nenne die abzubrechenden Dateien in cancelFileNames und ordne die übrigen wie gewohnt ihren Feldern zu. Die zugeordneten Dateien werden verarbeitet, auch wenn cancelUpload=true ist.
${phaseRules}`,
      parameters: {
        type: 'object',
        properties: {
          Schülerabgaben: {
            type: 'array',
            items: { type: 'string' },
            description:
              'Dateinamen der Schülerabgaben (optional — nur für die Bewertung nötig, nicht für die Verarbeitung der anderen Dateien)',
          },
          Musterlösung: {
            type: 'string',
            description: 'Dateiname der Musterlösung (optional)',
          },
          Bewertungsschema: {
            type: 'string',
            description: 'Dateiname des Bewertungsschemas (optional)',
          },
          ...(blankExamAllowed
            ? {
                LeereKlausur: {
                  type: 'string',
                  description: 'Dateiname der leeren Klausur (optional, nur solange die Struktur nicht erkannt ist)',
                },
                leereKlausurNachgefragt: {
                  type: 'boolean',
                  description:
                    'MUSS gesetzt werden: true, wenn du den Nutzer bereits ausdrücklich gefragt hast, ob er eine leere Klausur hat, oder er dir gesagt hat, dass er keine hat.',
                },
              }
            : {}),
          cancelUpload: {
            type: 'boolean',
            description: 'Wenn true: Verarbeitung abbrechen und die betroffenen unverarbeiteten Dateien löschen.',
          },
          cancelFileNames: {
            type: 'array',
            items: { type: 'string' },
            description:
              'Optionale Dateinamen, die bei cancelUpload=true gelöscht werden. Leer = alle unverarbeiteten Dateien.',
          },
          userExplicitlyConfirmed: {
            type: 'boolean',
            description:
              'MUSS true sein, wenn der Nutzer die Zuordnung im Chat ausdrücklich genannt oder bestätigt hat (z. B. nach send_assignment_confirmation_message). Sonst false.',
          },
        },
        required: ['cancelUpload', ...(blankExamAllowed ? ['leereKlausurNachgefragt'] : [])],
      },
    },
  };
}

/**
 * Writes an upload problem into the chat as a normal assistant message. Used for failures that only
 * surface after the tool has already returned (the processing runs in the background).
 */
async function pushAssistantUploadIssueMessage(conversationId: string, content: string): Promise<void> {
  if (!conversationId || !content.trim()) return;

  try {
    const conversationCollection = await getCollection(Conversation);

    const timeoutMs = 20_000;
    const pollIntervalMs = 200;
    const startedAt = Date.now();

    // Avoid a race where generateAnswer still persists its final `messages` array
    // and overwrites background messages that are pushed too early.
    while (Date.now() - startedAt < timeoutMs) {
      const currentConversation = await conversationCollection.findOne(
        { _id: conversationId },
        { projection: { thinking: 1 } as any },
      );
      if (currentConversation?.thinking !== true) {
        break;
      }
      await new Promise<void>(resolve => {
        setTimeout(resolve, pollIntervalMs);
      });
    }

    await conversationCollection.updateOne(
      { _id: conversationId },
      {
        $push: {
          messages: new AssistantMessage({ content }).toObject() as any,
        },
      },
    );
  } catch (pushErr) {
    logger.error({ err: pushErr, context: 'handleProcessFiles' }, 'Konnte Upload-Fehler nicht in Chat schreiben');
  }
}

function backgroundFailureMessage(): string {
  return i18next.t('upload.backgroundUploadFailedMessage', {
    defaultValue:
      'Beim Verarbeiten der hochgeladenen Dateien ist ein Problem aufgetreten. Bitte prüfe deine Dateien und versuche den Upload erneut.',
  });
}

/**
 * What the agent is told once the upload has been handed to the background. The work runs detached, so
 * this is derived from the exam's state plus what this call contains.
 *
 * It answers exactly two questions, in that order: what is running NOW, and what is the ONE thing that
 * has to happen next. Both are single statements — never a list of independent notes. An earlier version
 * concatenated one note per condition and the agent faithfully relayed the result: it told the teacher
 * that processing had started and that nothing could be processed yet, and asked twice for submissions.
 * Anything the agent must not claim (a running progress bar, a finished evaluation) is left out here
 * rather than corrected afterwards.
 */
function buildStartedMessage(
  exam: Exam,
  assignment: FileAssignment,
  extraNotes: Array<string | undefined> = [],
): string {
  const examState = exam.examState;
  const hasSolutionFile = !!assignment.masterSolution || !!assignment.gradingScheme;
  const structureConfirmed = isStructureConfirmed(examState);

  // Work that actually starts now. Storing a master solution that cannot be evaluated yet is NOT work
  // in progress, and must not be announced as such — there is nothing to watch in the status bar.
  const startsStructure = !!assignment.blankExam;
  const startsSplit = assignment.submissions.length > 0;
  const startsSolutionExtraction = hasSolutionFile && structureConfirmed;
  const runningNow = startsStructure || startsSplit || startsSolutionExtraction;

  const parts: string[] = [];

  if (runningNow) {
    const running = [
      startsStructure
        ? i18next.t('upload.runningStructure', {
            defaultValue: 'die Aufgabenstruktur wird aus der leeren Klausur erkannt',
          })
        : '',
      startsSplit ? i18next.t('upload.runningSplit', { defaultValue: 'die Schülerabgaben werden aufgeteilt' }) : '',
      startsSolutionExtraction
        ? i18next.t('upload.runningSolutions', {
            defaultValue: 'Musterlösung bzw. Bewertungsschema werden gegen die Aufgabenstruktur ausgewertet',
          })
        : '',
      startsSolutionExtraction && !!assignment.masterSolution && !!examState?.gradingSchemeSourceState
        ? i18next.t('upload.runningSchemeRefine', {
            defaultValue: 'das bestehende Bewertungsschema wird danach automatisch an die neue Musterlösung angepasst',
          })
        : '',
    ].filter(Boolean);

    parts.push(
      i18next.t('upload.startedRunning', {
        running: running.join(', '),
        defaultValue: `SYSTEM INFO: Verarbeitung im Hintergrund gestartet: ${running.join(
          ', ',
        )}. Der Fortschritt ist in der Statusleiste rechts zu sehen.`,
      }),
    );
  } else {
    parts.push(
      i18next.t('upload.startedStoredOnly', {
        defaultValue:
          'SYSTEM INFO: Die Datei ist gespeichert. Es läuft aber noch KEINE Verarbeitung — behaupte nicht, dass etwas verarbeitet wird, und verweise nicht auf die Statusleiste.',
      }),
    );
  }

  // What still has to happen, in the order it happens. These are distinct actions at distinct times, so
  // naming two is a sequence, not a repetition — but the fallback only applies when nothing else is
  // pending, so the teacher is never asked for submissions twice in one message. The "no structure source
  // at all" case cannot reach this point: the handler refuses such a call before anything is processed.
  const next: string[] = [];

  if (startsSplit) {
    next.push(
      i18next.t('upload.nextConfirmSplit', {
        defaultValue: 'Die Bestätigung der Aufteilung erscheint anschließend im Chat.',
      }),
    );
  }
  if (hasSolutionFile && !structureConfirmed) {
    next.push(
      i18next.t('upload.nextAwaitStructureConfirmation', {
        defaultValue:
          'Die Auswertung von Musterlösung bzw. Bewertungsschema startet automatisch, sobald der Nutzer die erkannte Aufgabenstruktur bestätigt hat.',
      }),
    );
  }
  if (next.length === 0 && (examState?.uploadProgress?.submissionsFound ?? 0) === 0) {
    next.push(
      i18next.t('upload.nextNeedSubmissions', {
        defaultValue: 'Für die Bewertung fehlen später noch die Schülerabgaben — erwähne das einmal kurz.',
      }),
    );
  }

  parts.push(...next);

  // What did NOT happen in this call (a rejected blank exam, a cancelled file) — stated once, after the
  // running/next block, so the agent can relay it without contradicting what it just said.
  parts.push(...extraNotes.filter((note): note is string => !!note));

  parts.push(
    i18next.t('upload.startedStyleRule', {
      defaultValue:
        'Fasse das in EINER kurzen Antwort zusammen (höchstens drei Sätze, keine Aufzählung). Sage jede Information genau einmal und wiederhole nichts, was du oben schon gesagt hast.',
    }),
  );

  return parts.join(' ');
}

export async function handleProcessFiles(exam: Exam, toolCallArgs: any): Promise<object> {
  const conversationId = exam.conversationId;
  const examId = exam._id?.toString();
  logger.info(
    { conversationId, toolCallArgKeys: Object.keys(toolCallArgs ?? {}), context: 'handleProcessFiles' },
    'Tool aufgerufen',
  );

  if (!examId) {
    logger.error({ context: 'handleProcessFiles' }, 'Fehler: Keine Klausur-ID vorhanden');
    return {
      content: i18next.t('upload.noExamId', { defaultValue: 'Fehler: Keine Klausur-ID für Initial-Upload vorhanden.' }),
    };
  }

  const examState = exam.examState;
  const pendingFiles: any[] = examState?.pendingFiles ?? [];

  const blankExamAllowed = canProcessBlankExam(examState);
  const assignment = parseAssignmentArgs(toolCallArgs, { allowBlankExam: blankExamAllowed });

  // The blank-exam parameter isn't even offered once the structure exists, but the agent may still
  // supply it from an earlier turn's schema. `parseAssignmentArgs` has dropped it; the file stays
  // pending so it can be reassigned, and the agent is told about it. Everything else in the same call
  // is still processed — one unusable file must not take a legitimate upload down with it.
  const rejectedBlankExam =
    !blankExamAllowed && typeof toolCallArgs?.LeereKlausur === 'string' && toolCallArgs.LeereKlausur.trim()
      ? String(toolCallArgs.LeereKlausur).trim()
      : undefined;
  const blankExamRejectionNote = rejectedBlankExam
    ? i18next.t('upload.blankExamNoLongerPossible', {
        file: rejectedBlankExam,
        defaultValue: `'${rejectedBlankExam}' wurde NICHT verarbeitet: eine leere Klausur ist nur möglich, solange die Aufgabenstruktur noch nicht erkannt ist. Teile dem Nutzer mit, dass die Datei unverarbeitet liegen bleibt, und frage ihn, ob sie stattdessen als Schülerabgabe, Musterlösung oder Bewertungsschema verarbeitet werden soll.`,
      })
    : undefined;

  // --- cancellation ---
  // A cancellation regularly arrives together with an assignment ("die leere Klausur brauche ich nicht,
  // verarbeite aber das Bewertungsschema") — one turn, one tool call, both intents. Returning on
  // `cancelUpload` alone, as this did, silently dropped the assignment: the declared files were still in
  // the buffer, so nothing failed, and the agent reported only the cancellation.
  //
  // Only the cancel-ONLY case is answered here. A call that also assigns files runs the deletion further
  // down, once the validations have passed — a call that gets blocked must leave the buffer untouched, or
  // the agent would be told "nothing was processed" about files it can no longer name.
  const cancelRequested = !!toolCallArgs?.cancelUpload;
  const cancelFileNames = Array.isArray(toolCallArgs?.cancelFileNames) ? toolCallArgs.cancelFileNames : [];

  if (cancelRequested && assignmentEntries(assignment).length === 0) {
    const cancelTargets = selectPendingCancelTargets(pendingFiles, cancelFileNames);
    const deletedCount = await deletePendingFileEntries(examId, cancelTargets);
    logger.info(
      {
        examId,
        deletedCount,
        cancelledPendingIds: cancelTargets.map(file => String(file?.id ?? '')),
        context: 'handleProcessFiles',
      },
      'Upload abgebrochen, Pending-Dateien gelöscht',
    );
    return {
      content: [
        i18next.t('upload.reuploadCancelled', { defaultValue: 'SYSTEM INFO: Upload-Abbruch verarbeitet.' }),
        blankExamRejectionNote,
      ]
        .filter(Boolean)
        .join(' '),
    };
  }

  // Any single role is enough to do something useful — submissions are needed for grading, not for
  // processing. Only a call that declares no usable file at all has nothing to run.
  if (assignmentEntries(assignment).length === 0) {
    return {
      content:
        blankExamRejectionNote ??
        i18next.t('upload.nothingToProcess', {
          defaultValue:
            'SYSTEM BLOCK: Es wurde keine Datei zur Verarbeitung angegeben. Gib die Dateinamen in den passenden Feldern an (Schülerabgaben, Musterlösung, Bewertungsschema) oder setze cancelUpload=true, wenn der Nutzer abbrechen möchte.',
        }),
    };
  }

  // --- confidence check: can we trust the filenames, or does the teacher have to confirm? ---
  if (!toolCallArgs?.userExplicitlyConfirmed) {
    const lowConfidenceFiles = findLowConfidenceAssignments(assignment);
    logger.info(
      { lowConfidenceCount: lowConfidenceFiles.length, context: 'handleProcessFiles' },
      'Confidence-Pruefung der Dateizuordnung',
    );

    if (lowConfidenceFiles.length > 0) {
      logger.info({ context: 'handleProcessFiles' }, 'SYSTEM BLOCK: Confidence zu niedrig');

      return {
        content:
          'System BLOCK: Die Zuordnung der Dateien ist unklar. Bitte bestätige die Zuordnung mit dem User im Chat indem du sofort hier nach in deinem nächsten Zug send_assignment_confirmation_message aufrufst (antworte nicht zuerst sondern rufe direkt das Tool auf):',
      };
    }
  }

  // --- do the declared files exist, and do they match the last proposal the teacher saw? ---
  const missingFiles = findMissingPendingFiles(assignment, pendingFiles);
  if (missingFiles.length > 0) {
    return { content: formatMissingFilesBlock(missingFiles, listPendingFileNames(pendingFiles)) };
  }

  if (pendingFiles.length > 0) {
    try {
      const conv = await (await getCollection(Conversation)).findOne({ _id: conversationId });
      const messages = Array.isArray((conv as any)?.messages) ? (conv as any).messages : [];

      const roleMap = buildProposalRoleMap(messages, { allowBlankExam: blankExamAllowed });
      logger.info(
        { roleMapKeys: Object.keys(roleMap).join(','), context: 'handleProcessFiles' },
        'Indexierte Dateinamen aus den letzten Zuordnungsvorschlaegen',
      );

      const mismatches = findAssignmentMismatches(assignment, roleMap);
      if (mismatches.length > 0) {
        return { content: formatMismatchBlock(mismatches) };
      }
    } catch (err) {
      logger.error({ err, examId, context: 'handleProcessFiles' }, 'Error applying proposal mappings');
    }
  }

  // --- is there anything the task structure can be read from? ---
  // A master solution or grading scheme is only worth processing once there is one: without it the file
  // would be stored, its upload-buffer entry consumed, and nothing would ever evaluate it. Worse, the
  // teacher would then be asked to upload it again, because the name no longer resolves. So the call is
  // refused as a whole, everything stays pending, and the agent collects the missing piece first.
  if (!hasStructureSource(exam) && !assignment.blankExam && assignment.submissions.length === 0) {
    const keptFiles = listPendingFileNames(pendingFiles);
    logger.info(
      { examId, keptPendingIds: pendingFiles.map(file => String(file?.id ?? '')), context: 'handleProcessFiles' },
      'SYSTEM BLOCK: Keine Grundlage für die Aufgabenstruktur',
    );

    return {
      content: i18next.t('upload.needStructureSourceBlock', {
        files: keptFiles.join(', '),
        defaultValue: `SYSTEM BLOCK: Es wurde nichts verarbeitet. Musterlösung und Bewertungsschema können erst ausgewertet werden, wenn die Aufgabenstruktur dieser Klausur vorliegt — dafür fehlt noch die leere Klausur oder die Schülerabgaben. Bitte den Nutzer um genau eines von beiden (die Schülerabgaben werden ohnehin für die Bewertung gebraucht) und sage ihm ausdrücklich, dass die bereits hochgeladenen Dateien erhalten bleiben und NICHT erneut hochgeladen werden müssen. Diese Dateien liegen weiterhin im Upload-Puffer: ${keptFiles.join(
          ', ',
        )}. Sobald die fehlende Datei da ist, rufe dieses Tool einmal mit allen Dateien zusammen auf.`,
      }),
    };
  }

  // The call is going through, so a cancellation in it can now be applied. Scoped to the named files and
  // never to the ones this call assigns: with no `cancelFileNames` a blanket cancel means "the rest", and
  // deleting an assigned file here would strand the upload that is about to resolve it.
  const cancelNotes: string[] = [];
  if (cancelRequested) {
    const cancelTargets = selectPendingCancelTargets(
      pendingFiles,
      cancelFileNames,
      collectAssignedPendingIds(assignment, pendingFiles),
    );
    const cancelledNames = cancelTargets.map(file => String(file?.name ?? '')).filter(Boolean);
    const deletedCount = await deletePendingFileEntries(examId, cancelTargets);

    logger.info(
      {
        examId,
        deletedCount,
        cancelledPendingIds: cancelTargets.map(file => String(file?.id ?? '')),
        context: 'handleProcessFiles',
      },
      'Upload teilweise abgebrochen, Pending-Dateien gelöscht',
    );

    cancelNotes.push(
      cancelledNames.length
        ? i18next.t('upload.cancelledAlongsideProcessing', {
            files: cancelledNames.join(', '),
            defaultValue: `Abgebrochen und gelöscht wurde dabei: ${cancelledNames.join(', ')}.`,
          })
        : i18next.t('upload.cancelledNothingMatched', {
            defaultValue:
              'Für den Abbruch wurde keine passende unverarbeitete Datei gefunden — es wurde nichts gelöscht.',
          }),
    );
  }

  // Processing runs detached: the split agent and the extraction steps take minutes, and the agent
  // must be free to talk to the user meanwhile. Failures are pushed into the chat afterwards.
  logger.info({ examId, context: 'handleProcessFiles' }, 'Starte Hintergrundverarbeitung');
  void (async () => {
    try {
      const success = await processUploadedFiles(assignment, examId, conversationId);
      if (!success) {
        logger.warn({ examId }, 'Hintergrundverarbeitung konnte nicht vollstaendig abgeschlossen werden');
        await pushAssistantUploadIssueMessage(conversationId, backgroundFailureMessage());
      }
    } catch (backgroundErr) {
      logger.error({ err: backgroundErr, examId }, 'Fehler in der Hintergrundverarbeitung der Dateien');
      await pushAssistantUploadIssueMessage(
        conversationId,
        isUserFacingUploadError(backgroundErr) ? backgroundErr.userMessage : backgroundFailureMessage(),
      );
    }
  })();

  const startedMessage = buildStartedMessage(exam, assignment, [...cancelNotes, blankExamRejectionNote]);

  logger.info({ examId, context: 'handleProcessFiles' }, 'Rueckgabe');
  return { content: startedMessage };
}

/**
 * Available whenever there is something to process. Deliberately NOT gated on the exam's phase: the
 * predecessors of this tool were split into an "initial" and a "re-upload" variant whose conditions
 * left a gap (submissions uploaded, structure extraction not finished yet) in which neither was
 * offered and an upload could not be processed at all.
 */
export function processFilesAvailability(examState: ExamState): boolean {
  return (examState?.pendingFiles?.length ?? 0) > 0;
}
